import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { registerPlugin } from '@capacitor/core';
import { StorageService } from './storage.service';
import { getDistance } from 'ol/sphere';
import { DateUtilsService } from './date-utils.service';
import { AlertController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

export interface TrackPoint {
  lat: number;
  lng: number;
  ele?: number;
  time: string;
}

export interface RecordingStats {
  durationMs: number;
  distanceKm: number;
  currentSpeedKph: number;
  averageSpeedKph: number;
  elevationGainMeters: number;
}

export type RecordingState = 'idle' | 'ready' | 'recording' | 'paused';

@Injectable({
  providedIn: 'root'
})
export class ActivityRecorderService {
  private BackgroundGeolocation = registerPlugin<any>('BackgroundGeolocation');
  private _isAvailable$ = new BehaviorSubject<boolean>(true);
  public isAvailable$ = this._isAvailable$.asObservable();
  public get isCurrentlyAvailable(): boolean { return this._isAvailable$.value; }

  private _error$ = new BehaviorSubject<string | null>(null);
  public error$ = this._error$.asObservable();

  private _state$ = new BehaviorSubject<RecordingState>('idle');
  public state$ = this._state$.asObservable();

  private _stats$ = new BehaviorSubject<RecordingStats>({
    durationMs: 0,
    distanceKm: 0,
    currentSpeedKph: 0,
    averageSpeedKph: 0,
    elevationGainMeters: 0
  });
  public stats$ = this._stats$.asObservable();

  private sessionPoints: TrackPoint[] = [];
  private sessionAnimalIds: number[] = [];
  private watchId: string | null = null;
  private startTime: number | null = null;
  private accumulatedDuration: number = 0;
  private timerSubscription: Subscription | null = null;

  constructor(
    private storage: StorageService,
    private dateUtils: DateUtilsService,
    private http: HttpClient,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private translate: TranslateService
  ) {
    this.checkAvailability();
    this.restoreSession();
  }

  private async checkAvailability() {
    try {
      const geolocationModule = await import('@capacitor/geolocation');
      const Geolocation = geolocationModule.Geolocation;
      this._isAvailable$.next(!!Geolocation);
    } catch {
      this._isAvailable$.next(false);
    }
  }

  async prepare(animalIds: number[] = []) {
    if (this._state$.value !== 'idle') return;

    this.sessionPoints = [];
    this.sessionAnimalIds = animalIds;
    this.accumulatedDuration = 0;
    this.startTime = null;
    this._state$.next('ready');
    this.saveSession();
  }

  async start(animalIds: number[] = []) {
    if (this._state$.value !== 'idle' && this._state$.value !== 'ready') return;

    if (this._state$.value === 'idle') {
      this.sessionPoints = [];
      this.sessionAnimalIds = animalIds;
      this.accumulatedDuration = 0;
    }

    this.startTime = Date.now();
    this._state$.next('recording');

    this.startWatching();
    this.startTimer();
    this.saveSession();
  }

  async pause() {
    if (this._state$.value !== 'recording') return;

    this.stopWatching();
    this.stopTimer();
    
    if (this.startTime) {
      this.accumulatedDuration += Date.now() - this.startTime;
      this.startTime = null;
    }
    
    this._state$.next('paused');
    this.saveSession();
  }

  async resume() {
    if (this._state$.value !== 'paused') return;

    this.startTime = Date.now();
    this._state$.next('recording');
    
    this.startWatching();
    this.startTimer();
    this.saveSession();
  }

  async stop(): Promise<{ points: TrackPoint[], stats: RecordingStats, gpx: string, animalIds: number[] }> {
    const finalStats = { ...this._stats$.value };
    const finalPoints = [...this.sessionPoints];
    const finalAnimalIds = [...this.sessionAnimalIds];
    
    this.stopWatching();
    this.stopTimer();
    
    this._state$.next('idle');
    this.clearSession();

    // Correct elevation gain with API for better accuracy
    try {
      const correctedPoints = await this.correctElevationWithAPI(finalPoints);
      if (correctedPoints.length > 0) {
        // Re-calculate elevation gain from corrected points
        let newGain = 0;
        for (let i = 1; i < correctedPoints.length; i++) {
          if (correctedPoints[i].ele !== undefined && correctedPoints[i-1].ele !== undefined) {
             const diff = (correctedPoints[i].ele || 0) - (correctedPoints[i-1].ele || 0);
             if (diff > 1.5) newGain += diff;
          }
        }
        finalStats.elevationGainMeters = Math.round(newGain);
      }
    } catch (e) {
      console.warn('Elevation correction failed, using GPS data:', e);
    }
    
    const gpx = this.generateGPX(finalPoints);
    
    return { points: finalPoints, stats: finalStats, gpx, animalIds: finalAnimalIds };
  }

  /**
   * Corrects elevation items using a DEM (Digital Elevation Model) API.
   * OpenTopoData is used here (public instance).
   */
  public async correctElevationWithAPI(points: TrackPoint[]): Promise<TrackPoint[]> {
    if (points.length === 0) return [];
    
    const batchSize = 100;
    const resultPoints = [...points];

    for (let i = 0; i < points.length; i += batchSize) {
      const batch = points.slice(i, i + batchSize);
      const locations = batch.map(p => `${p.lat},${p.lng}`).join('|');
      
      try {
        const response: any = await this.http.get(`https://api.opentopodata.org/v1/aster30m?locations=${locations}`).toPromise();
        if (response && response.results) {
          response.results.forEach((res: any, idx: number) => {
            if (res.elevation !== null) {
              resultPoints[i + idx].ele = res.elevation;
            }
          });
        }
      } catch (err) {
        console.error('Error fetching elevation for batch:', err);
        // Continue with next batch or stop? For now we continue.
      }
    }
    return resultPoints;
  }

  private firstPointReceived = false;

  private async startWatching() {
    try {
      const geolocationModule = await import('@capacitor/geolocation');
      const Geolocation = geolocationModule.Geolocation;

      const permissions = await Geolocation.checkPermissions();
      if (permissions.location !== 'granted') {
          const confirmed = await this.showLocationDisclosure();
          if (!confirmed) return;
          await Geolocation.requestPermissions();
      }

      this.firstPointReceived = false;
      this.showGpsToast();

      // Use BackgroundGeolocation to ensure tracking when the app is in background or screen is off
      this.watchId = await this.BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: "Enregistrement de votre activité en cours...",
          backgroundTitle: "Son Espace Santé",
          requestPermissions: false,
          stale: false,
          distanceFilter: 3 // GPS update every 3 meters for higher accuracy
        },
        (position: any | null, err: any | null) => {
          if (err) {
            console.error('Background GPS Error:', err);
            this._error$.next(err.message || 'GPS Unavailable');
            return;
          }
          if (position) {
            this._error$.next(null);
            this.addPointFromBackground(position);
          }
        }
      );
    } catch (e) {
      console.warn('Background Geolocation failed to start.', e);
      this._error$.next('Background GPS failed');
    }
  }

  private async showLocationDisclosure(): Promise<boolean> {
    return new Promise(async (resolve) => {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('LOCATION_PERMISSION_TITLE'),
        message: this.translate.instant('LOCATION_PERMISSION_MESSAGE'),
        backdropDismiss: false,
        buttons: [
          {
            text: this.translate.instant('Cancel'),
            role: 'cancel',
            handler: () => resolve(false)
          },
          {
            text: this.translate.instant('CONTINUE'),
            handler: () => resolve(true)
          }
        ]
      });
      await alert.present();
    });
  }

  private async showGpsToast() {
    const toast = await this.toastCtrl.create({
      message: this.translate.instant('GPS_RECORDING_ACTIVE'),
      duration: 3000,
      position: 'top',
      color: 'primary',
      icon: 'navigate-outline',
    });
    await toast.present();
  }

  private addPointFromBackground(pos: any) {
    // BackgroundGeolocation returns flat fields, normalize to match addPoint(position.coords)
    const normalizedPosition = {
      coords: {
        latitude: pos.latitude,
        longitude: pos.longitude,
        altitude: pos.altitude,
        speed: pos.speed
      }
    };
    this.addPoint(normalizedPosition);
  }

  private stopWatching() {
    if (this.watchId) {
      this.BackgroundGeolocation.removeWatcher({ id: this.watchId });
      this.watchId = null;
    }
  }

  private addPoint(position: any) {
    const newPoint: TrackPoint = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      ele: position.coords.altitude || undefined,
      time: new Date().toISOString()
    };

    if (this.sessionPoints.length > 0) {
      const prev = this.sessionPoints[this.sessionPoints.length - 1];
      const dist = getDistance([prev.lng, prev.lat], [newPoint.lng, newPoint.lat]);
      
      // Update stats
      const currentStats = this._stats$.value;
      const newDistKm = currentStats.distanceKm + (dist / 1000);
      
      // Basic speed (m/s to km/h)
      const speedKph = position.coords.speed ? (position.coords.speed * 3.6) : 0;

      // Elevation Gain calculation with threshold to filter noise
      let newElevationGain = currentStats.elevationGainMeters;
      if (newPoint.ele !== undefined && prev.ele !== undefined) {
        const eleDiff = newPoint.ele - prev.ele;
        // Only count positive gain and filter out small variations (GPS noise)
        // A threshold of 2 meters is often used for real-time filtering without DEM
        if (eleDiff > 1.5) { 
          newElevationGain += eleDiff;
        }
      }

      this._stats$.next({
        ...currentStats,
        distanceKm: Number(newDistKm.toFixed(3)),
        currentSpeedKph: Number(speedKph.toFixed(1)),
        elevationGainMeters: Math.round(newElevationGain)
      });
    }

    this.sessionPoints.push(newPoint);
    this.saveSession();
  }

  private startTimer() {
    if (this.timerSubscription) this.timerSubscription.unsubscribe();
    this.timerSubscription = interval(1000).subscribe(() => {
      this.updateDuration();
    });
  }

  private stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  private updateDuration() {
    let currentDuration = this.accumulatedDuration;
    if (this.startTime) {
      currentDuration += Date.now() - this.startTime;
    }
    
    const stats = this._stats$.value;
    const avgSpeed = stats.distanceKm > 0 ? (stats.distanceKm / (currentDuration / 3600000)) : 0;

    this._stats$.next({
      ...stats,
      durationMs: currentDuration,
      averageSpeedKph: Number(avgSpeed.toFixed(1))
    });
  }

  private async saveSession() {
    const data = {
      state: this._state$.value,
      points: this.sessionPoints,
      animalIds: this.sessionAnimalIds,
      accumulatedDuration: this.accumulatedDuration,
      startTime: this.startTime,
      stats: this._stats$.value
    };
    this.storage.set('active_recording_session', data);
  }

  private async restoreSession() {
    try {
      // StorageService.get handles platform-specific timeouts (2s for browser, 10s for mobile)
      const data = await this.storage.get('active_recording_session') as any;
      
      if (data) {
        this.sessionPoints = data.points || [];
        this.sessionAnimalIds = data.animalIds || [];
        this.accumulatedDuration = data.accumulatedDuration || 0;
        this.startTime = data.startTime;
        this._state$.next(data.state || 'idle');
        this._stats$.next(data.stats || this._stats$.value);

        if (this._state$.value === 'recording') {
          this.startWatching();
          this.startTimer();
        }
      }
    } catch (e) {
      console.warn('ActivityRecorderService: Session restoration skipped (storage unavailable).', e);
    }
  }

  private clearSession() {
    this.storage.remove('active_recording_session');
    this.sessionPoints = [];
    this.accumulatedDuration = 0;
    this.startTime = null;
    this._stats$.next({
      durationMs: 0,
      distanceKm: 0,
      currentSpeedKph: 0,
      averageSpeedKph: 0,
      elevationGainMeters: 0
    });
  }

  private generateGPX(points: TrackPoint[]): string {
    let gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="SonEspaceSante" xmlns="http://www.topografix.com/GPX/1/1">
  <trk>
    <name>Activity ${this.dateUtils.toString(new Date())}</name>
    <trkseg>`;

    points.forEach(p => {
      gpx += `
      <trkpt lat="${p.lat}" lon="${p.lng}">
        ${p.ele ? `<ele>${p.ele}</ele>` : ''}
        <time>${p.time}</time>
      </trkpt>`;
    });

    gpx += `
    </trkseg>
  </trk>
</gpx>`;
    return gpx;
  }
}
